import { useState, useEffect } from 'react';

export type DeviceType = 'desktop' | 'tablet' | 'mobile';
export type OSType = 'iOS' | 'Android' | 'Windows' | 'MacOS' | 'Linux' | 'Unknown';

interface DeviceInfo {
  type: DeviceType;
  browserName: string;
  isDesktop: boolean;
  isTablet: boolean;
  isMobile: boolean;
  userAgent: string;
  os: OSType;
}

export const useDevice = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    // Valeurs par défaut côté serveur
    if (typeof window === 'undefined') {
      return {
        type: 'desktop',
        browserName: 'unknown',
        isDesktop: true,
        isTablet: false,
        isMobile: false,
        userAgent: '',
        os: 'Unknown'
      };
    }

    const ua = navigator.userAgent;
    let deviceType: DeviceType = 'desktop';
    let browserName = 'unknown';
    let os: OSType = 'Unknown';

    // Détecter le type d'appareil
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      deviceType = 'tablet';
    } else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      deviceType = 'mobile';
    }

    // Détecter le navigateur
    if (ua.indexOf("Firefox") > -1) {
      browserName = "Firefox";
    } else if (ua.indexOf("SamsungBrowser") > -1) {
      browserName = "Samsung Browser";
    } else if (ua.indexOf("Opera") > -1 || ua.indexOf("OPR") > -1) {
      browserName = "Opera";
    } else if (ua.indexOf("Edg") > -1) {
      browserName = "Edge";
    } else if (ua.indexOf("Chrome") > -1) {
      browserName = "Chrome";
    } else if (ua.indexOf("Safari") > -1) {
      browserName = "Safari";
    }

    // Détecter le système d'exploitation
    if (/iPhone|iPad|iPod/.test(ua)) {
      os = 'iOS';
    } else if (/Android/.test(ua)) {
      os = 'Android';
    } else if (/Windows/.test(ua)) {
      os = 'Windows';
    } else if (/Mac OS X/.test(ua)) {
      os = 'MacOS';
    } else if (/Linux/.test(ua)) {
      os = 'Linux';
    }

    return {
      type: deviceType,
      browserName,
      isDesktop: deviceType === 'desktop',
      isTablet: deviceType === 'tablet',
      isMobile: deviceType === 'mobile',
      userAgent: ua,
      os
    };
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let newType: DeviceType = deviceInfo.type;

      if (width >= 1024) {
        newType = 'desktop';
      } else if (width >= 768) {
        newType = 'tablet';
      } else {
        newType = 'mobile';
      }

      if (newType !== deviceInfo.type) {
        setDeviceInfo(prev => ({
          ...prev,
          type: newType,
          isDesktop: newType === 'desktop',
          isTablet: newType === 'tablet',
          isMobile: newType === 'mobile'
        }));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [deviceInfo.type]);

  return deviceInfo;
};